/**
 * Sheet „Objekt umhängen" (Spec 02b, „Oberfläche · Umhängen").
 *
 * Liegt in `features/objects`, nicht in `features/properties`: Der Auslöser
 * `<art>-move` sitzt im Kopf der Objektansicht (`routes/ObjectDetail.tsx`),
 * und Routen dürfen mehrere `features` zusammensetzen — anders als die
 * Archivierungsrückfrage, deren Auslöser im Reiter *Stammdaten* sitzt und die
 * deshalb in `features/properties` liegt (Spec 02b, „Was 02b an 02a ändert",
 * Punkt 5; `02a: 03-frontend.md`).
 *
 * Der Server entscheidet allein, welche Ziele zulässig sind
 * (`GET /entities/{id}/move-targets`) — der Client filtert nichts nach (I9).
 * Deshalb kein Auswahlfeld mit Client-Logik, sondern eine Liste zum Anklicken:
 * eine Zeile wählen, dann „Verschieben" bestätigen.
 */

import { useRef, useState } from 'react'

import { EmptyState } from '@/components/states/EmptyState'
import { ErrorState } from '@/components/states/ErrorState'
import { SkeletonRows } from '@/components/states/Skeleton'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { ApiError } from '@/lib/api'
import { useMoveObject, useMoveTargets } from '@/features/objects/api'
import { FIELD_CLASS } from '@/components/FormField'

/** Dieselbe Feldgestaltung wie `features/properties/fields.tsx::FIELD_CLASS`
 * — hier noch einmal lokal, weil `features` einander nicht importieren
 * dürfen (`apps/web/CLAUDE.md`, „features kennen einander nicht"). */
interface MoveSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  /** Der Knoten, der umgehängt wird — nicht das Ziel. */
  readonly entityId: string
  readonly label: string
  /** Grundlage für `If-Match`. */
  readonly version: number
}

export function MoveSheet({ open, onOpenChange, entityId, label, version }: MoveSheetProps) {
  const searchRef = useRef<HTMLInputElement>(null)

  const [q, setQ] = useState('')
  const [selectedTargetId, setSelectedTargetId] = useState<string | null>(null)
  const [generalError, setGeneralError] = useState<string | undefined>(undefined)

  // Jedes Öffnen beginnt frisch (React-Muster „Adjusting state when a prop
  // changes", während des Renderns statt in einem Effekt) — dieselbe Technik
  // wie in `BuildingSheet`/`UnitSheet`.
  const [wasOpen, setWasOpen] = useState(open)
  if (open !== wasOpen) {
    setWasOpen(open)
    if (open) {
      setQ('')
      setSelectedTargetId(null)
      setGeneralError(undefined)
    }
  }

  const targetsQuery = useMoveTargets(entityId, { q, enabled: open })
  const moveMutation = useMoveObject()

  async function handleMove() {
    if (selectedTargetId === null) return
    setGeneralError(undefined)
    try {
      await moveMutation.mutateAsync({ entityId, parentId: selectedTargetId, ifMatch: String(version) })
      // Sheet schließt, der Nutzer bleibt auf dem Objekt — Pfad und Baum
      // aktualisieren sich über die Präfix-Invalidierung in `useMoveObject`.
      onOpenChange(false)
    } catch (error) {
      setGeneralError(error instanceof ApiError ? error.message : 'Die Verbindung zum Server ist unterbrochen.')
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          searchRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[480px] md:data-[side=right]:!max-w-[480px]"
      >
        <div data-testid="move-sheet" className="flex h-full flex-col">
          <SheetHeader>
            <SheetTitle className="text-section font-(--font-weight-section) text-text">
              „{label}" verschieben
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-1 flex-col gap-3 overflow-y-auto px-4">
            <label className="text-label text-muted flex flex-col gap-1" htmlFor="move-target-search">
              Ziel suchen
              <input
                id="move-target-search"
                ref={searchRef}
                data-testid="move-target-search"
                type="search"
                value={q}
                onChange={(event) => {
                  setQ(event.target.value)
                }}
                className={`text-text ${FIELD_CLASS}`}
              />
            </label>

            {targetsQuery.isPending && <SkeletonRows testId="move-targets-loading" rows={4} />}

            {targetsQuery.isError && (
              <ErrorState
                testId="move-targets-error"
                retryTestId="move-targets-retry"
                error={targetsQuery.error}
                onRetry={() => {
                  void targetsQuery.refetch()
                }}
              />
            )}

            {targetsQuery.isSuccess && targetsQuery.data.items.length === 0 && (
              <EmptyState
                testId="move-targets-empty"
                title="Kein zulässiges Ziel."
                hint="Eine Einheit hängt an einem Gebäude oder an einem Grundstück. Lege zuerst eines an."
              />
            )}

            {targetsQuery.isSuccess && targetsQuery.data.items.length > 0 && (
              <ul className="border-border rounded-card border">
                {targetsQuery.data.items.map((target) => (
                  <li key={target.id} className="border-border border-b last:border-b-0">
                    <button
                      type="button"
                      data-testid={`move-target-${target.id}`}
                      aria-pressed={selectedTargetId === target.id}
                      onClick={() => {
                        setSelectedTargetId(target.id)
                      }}
                      className={`flex min-h-(--tap-min) w-full flex-col items-start justify-center gap-0.5 px-3 py-1 text-left transition-colors duration-(--duration-fast) ease-(--ease-out) ${
                        selectedTargetId === target.id ? 'bg-accent/10' : 'hover:bg-bg'
                      }`}
                    >
                      <span className="text-body text-text">{target.label}</span>
                      <span className="text-label text-muted">{target.path_label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* Fester Hinweis — keine errechnete Aussage darüber, wer Zugriff
             * verliert (Spec 02b, „Oberfläche · Umhängen": „Keine errechnete
             * Aussage darüber, wer etwas verliert."). */}
            <p className="text-label text-muted">Zugriffe folgen dem neuen Elternobjekt.</p>
          </div>

          <SheetFooter>
            {generalError !== undefined && (
              <p data-testid="move-error" role="alert" className="text-label text-status-overdue">
                {generalError}
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                data-testid="move-cancel"
                onClick={() => {
                  onOpenChange(false)
                }}
                className={`text-text px-4 ${FIELD_CLASS}`}
              >
                Abbrechen
              </button>
              <button
                type="button"
                data-testid="move-save"
                disabled={selectedTargetId === null || moveMutation.isPending}
                aria-busy={moveMutation.isPending}
                onClick={() => void handleMove()}
                className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
              >
                Verschieben
              </button>
            </div>
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  )
}
